import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { MarketingNav } from "@/components/landing/MarketingNav";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle, Plug, RefreshCw, Settings, Zap } from "lucide-react";
import { SEOHead } from "@/components/SEOHead";
import { SchemaMarkup, generateBreadcrumbSchema, generateFAQSchema, organizationSchema, generateSoftwareAppSchema } from "@/components/SchemaMarkup";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import hubspotLogo from "@/assets/hubspot-logo-new.png";
import rdStationLogo from "@/assets/rd-station-logo.jpg";

const integrations = [
  {
    name: "HubSpot",
    logo: hubspotLogo,
    status: { en: "Available", pt: "Disponível" },
    desc: {
      en: "Full bi-directional sync with HubSpot CRM. Deals, contacts, companies, activities, and custom fields.",
      pt: "Sincronização bidirecional completa com HubSpot CRM. Deals, contatos, empresas, atividades e campos customizados.",
    },
    bullets: {
      en: ["Real-time deal sync", "Contact & company enrichment", "Activity tracking", "Custom field mapping"],
      pt: [
        "Sync de deals em tempo real",
        "Enriquecimento de contatos & empresas",
        "Rastreamento de atividades",
        "Mapeamento de campos customizados",
      ],
    },
  },
  {
    name: "RD Station CRM",
    logo: rdStationLogo,
    status: { en: "Available", pt: "Disponível" },
    desc: {
      en: "Native integration with RD Station CRM for Brazilian sales teams. Full pipeline and deal synchronization.",
      pt: "Integração nativa com RD Station CRM para times de vendas brasileiros. Sincronização completa de pipeline e deals.",
    },
    bullets: {
      en: ["Pipeline stage mapping", "Deal value & close date sync", "Contact data enrichment", "Custom field support"],
      pt: [
        "Mapeamento de estágios do pipeline",
        "Sync de valor e data de fechamento",
        "Enriquecimento de dados de contato",
        "Suporte a campos customizados",
      ],
    },
  },
];

const syncTable = {
  en: [
    { entity: "Deals", desc: "Value, stage, close date, owner, custom fields", frequency: "Real-time + daily" },
    { entity: "Contacts", desc: "Name, email, phone, company, position", frequency: "Real-time + daily" },
    { entity: "Companies", desc: "Name, industry, revenue, employees, address", frequency: "Daily" },
    { entity: "Activities", desc: "Emails, calls, meetings, notes, tasks", frequency: "Real-time + daily" },
    { entity: "Products", desc: "Product name, price, quantity per deal", frequency: "Daily" },
    { entity: "Custom Fields", desc: "Any custom field mapped in your CRM", frequency: "Real-time + daily" },
  ],
  pt: [
    { entity: "Deals", desc: "Valor, estágio, data de fechamento, responsável, campos custom", frequency: "Tempo real + diário" },
    { entity: "Contatos", desc: "Nome, email, telefone, empresa, cargo", frequency: "Tempo real + diário" },
    { entity: "Empresas", desc: "Nome, indústria, receita, funcionários, endereço", frequency: "Diário" },
    { entity: "Atividades", desc: "Emails, ligações, reuniões, notas, tarefas", frequency: "Tempo real + diário" },
    { entity: "Produtos", desc: "Nome do produto, preço, quantidade por deal", frequency: "Diário" },
    { entity: "Campos Custom", desc: "Qualquer campo customizado mapeado no seu CRM", frequency: "Tempo real + diário" },
  ],
};

const faqData = {
  en: [
    { q: "How long does it take to connect my CRM?", a: "Less than 5 minutes. You authorize via OAuth (HubSpot) or API key (RD Station), map your fields, and the first sync starts automatically. No coding or IT support required." },
    { q: "Is my CRM data safe?", a: "Yes. We use encrypted connections (TLS 1.3), never store your CRM credentials in plain text, and all data is processed within SOC 2 compliant infrastructure. Your data is never shared with third parties." },
    { q: "What happens if the sync fails?", a: "You'll receive an alert via email and Slack (if configured) with the error details. The system automatically retries failed syncs and keeps your last successful dataset available for analysis." },
    { q: "Can I choose which fields to sync?", a: "Absolutely. During setup, you map exactly which CRM fields correspond to RevOps AI fields. You can add, remove, or change mappings at any time without losing historical data." },
    { q: "Do you support other CRMs besides HubSpot and RD Station?", a: "Currently we offer native integrations with HubSpot and RD Station CRM. For other CRMs like Salesforce or Pipedrive, you can import data via CSV. We're continuously expanding our integration catalog." },
  ],
  pt: [
    { q: "Quanto tempo leva para conectar meu CRM?", a: "Menos de 5 minutos. Você autoriza via OAuth (HubSpot) ou chave de API (RD Station), mapeia seus campos, e a primeira sincronização começa automaticamente. Sem necessidade de código ou suporte de TI." },
    { q: "Meus dados do CRM estão seguros?", a: "Sim. Usamos conexões criptografadas (TLS 1.3), nunca armazenamos suas credenciais do CRM em texto puro, e todos os dados são processados em infraestrutura compatível com SOC 2. Seus dados nunca são compartilhados com terceiros." },
    { q: "O que acontece se a sincronização falhar?", a: "Você receberá um alerta por email e Slack (se configurado) com os detalhes do erro. O sistema automaticamente tenta novamente as sincronizações que falharam e mantém seu último dataset bem-sucedido disponível para análise." },
    { q: "Posso escolher quais campos sincronizar?", a: "Com certeza. Durante a configuração, você mapeia exatamente quais campos do CRM correspondem aos campos do RevOps AI. Você pode adicionar, remover ou alterar mapeamentos a qualquer momento sem perder dados históricos." },
    { q: "Vocês suportam outros CRMs além de HubSpot e RD Station?", a: "Atualmente oferecemos integrações nativas com HubSpot e RD Station CRM. Para outros CRMs como Salesforce ou Pipedrive, você pode importar dados via CSV. Estamos continuamente expandindo nosso catálogo de integrações." },
  ],
};

export default function IntegrationsMarketingPage() {
  const { i18n } = useTranslation();
  const isPt = i18n.language?.startsWith("pt");

  const faqs = isPt ? faqData.pt : faqData.en;
  const tableRows = isPt ? syncTable.pt : syncTable.en;

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={isPt ? "Integrações com HubSpot e RD Station | RevOps AI" : "Integrations with HubSpot & RD Station | RevOps AI"}
        description={isPt ? "Conecte seu CRM em minutos. Integração nativa com RD Station e HubSpot para extrair insights de receita automaticamente." : "Connect your CRM in minutes. Native integration with RD Station and HubSpot to automatically extract revenue insights."}
        path="/integrations"
      />
      <SchemaMarkup schema={[
        organizationSchema,
        generateSoftwareAppSchema(isPt ? 'pt-BR' : 'en'),
        generateBreadcrumbSchema([
          { name: 'Home', url: 'https://revops.digitais.ai/' },
          { name: isPt ? 'Integrações' : 'Integrations', url: 'https://revops.digitais.ai/integrations' },
        ]),
        generateFAQSchema(faqs.map(f => ({ question: f.q, answer: f.a }))),
      ]} />
      <MarketingNav />
      <main>
        {/* Hero */}
        <section className="py-20 md:py-28 bg-[#F4F5FF]">
          <div className="container max-w-3xl mx-auto">
            <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: isPt ? 'Integrações' : 'Integrations' }]} />
            <div className="text-center">
              <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-[#4F6CFF]/10 rounded-full text-sm font-medium text-[#4F6CFF]">
                <Plug className="h-4 w-4" />
                {isPt ? "Integrações" : "Integrations"}
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-[#1A1F3C] mb-6">
                {isPt ? "Conecte seu CRM. Receba inteligência." : "Connect your CRM. Get intelligence."}
              </h1>
              <p className="text-lg text-[#1A1F3C]/60 mb-4">
                {isPt
                  ? "RevOps AI lê seus dados do CRM em tempo real e devolve diagnósticos, previsões e ações — direto nas ferramentas que seu time já usa."
                  : "RevOps AI reads your CRM data in real time and delivers diagnostics, forecasts, and actions — right in the tools your team already uses."}
              </p>
              <p className="text-base text-[#1A1F3C]/50 mb-8">
                {isPt
                  ? "Com integrações nativas, seu time elimina exportações manuais e planilhas desatualizadas. Cada deal, contato e atividade do CRM alimenta automaticamente dashboards de receita, forecasts matemáticos e recomendações de IA — garantindo que decisões comerciais sejam tomadas com dados frescos e confiáveis, não com suposições."
                  : "With native integrations, your team eliminates manual exports and outdated spreadsheets. Every deal, contact, and activity from your CRM automatically feeds revenue dashboards, mathematical forecasts, and AI recommendations — ensuring commercial decisions are made with fresh, reliable data, not assumptions."}
              </p>
              <Button asChild size="lg" className="bg-[#4F6CFF] hover:bg-[#3D5AE8] text-white">
                <Link to="/signup">
                  {isPt ? "Teste Grátis por 7 Dias" : "Start 7-Day Free Trial"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Integration Cards */}
        <section className="py-16 md:py-20">
          <div className="container max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1A1F3C] mb-12 text-center">
              {isPt ? "Integrações Disponíveis" : "Available Integrations"}
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {integrations.map((int) => (
                <div key={int.name} className="rounded-xl border border-[#1A1F3C]/10 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <img src={int.logo} alt={`${int.name} logo`} className="h-10 w-10 rounded-lg object-contain" width={40} height={40} />
                    <div>
                      <h3 className="text-xl font-bold text-[#1A1F3C]">{int.name}</h3>
                      <span className="text-xs font-medium text-[#00C896] bg-[#00C896]/10 px-2 py-0.5 rounded-full">
                        {isPt ? int.status.pt : int.status.en}
                      </span>
                    </div>
                  </div>
                  <p className="text-[#1A1F3C]/60 text-sm mb-4">{isPt ? int.desc.pt : int.desc.en}</p>
                  <ul className="space-y-2">
                    {(isPt ? int.bullets.pt : int.bullets.en).map((b, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-[#1A1F3C]/70">
                        <CheckCircle className="h-4 w-4 text-[#00C896] mt-0.5 flex-shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 md:py-20 bg-[#F4F5FF]/50">
          <div className="container max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1A1F3C] mb-4 text-center">
              {isPt ? "Como funciona a integração" : "How the integration works"}
            </h2>
            <p className="text-center text-[#1A1F3C]/60 mb-12 max-w-2xl mx-auto">
              {isPt
                ? "Em apenas 3 passos, seu CRM está conectado e gerando insights de receita automaticamente."
                : "In just 3 steps, your CRM is connected and generating revenue insights automatically."}
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: Settings,
                  step: "01",
                  title: isPt ? "Conecte seu CRM" : "Connect your CRM",
                  desc: isPt
                    ? "Autorize via OAuth (HubSpot) ou insira sua chave de API (RD Station). Não é necessário suporte de TI — o processo é self-service e leva menos de 2 minutos."
                    : "Authorize via OAuth (HubSpot) or enter your API key (RD Station). No IT support needed — the process is self-service and takes less than 2 minutes.",
                },
                {
                  icon: RefreshCw,
                  step: "02",
                  title: isPt ? "Mapeie seus campos" : "Map your fields",
                  desc: isPt
                    ? "Nosso sistema detecta automaticamente os campos do seu CRM e sugere mapeamentos inteligentes. Você revisa, ajusta se necessário, e confirma. Campos customizados são suportados nativamente."
                    : "Our system automatically detects your CRM fields and suggests intelligent mappings. You review, adjust if needed, and confirm. Custom fields are natively supported.",
                },
                {
                  icon: Zap,
                  step: "03",
                  title: isPt ? "Receba insights" : "Get insights",
                  desc: isPt
                    ? "A primeira sincronização completa acontece em minutos. A partir daí, seus dashboards, forecasts e recomendações de IA são alimentados automaticamente com dados sempre atualizados do CRM."
                    : "The first full sync happens in minutes. From there, your dashboards, forecasts, and AI recommendations are automatically powered by always-updated CRM data.",
                },
              ].map((step) => {
                const Icon = step.icon;
                return (
                  <div key={step.step} className="text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#4F6CFF] text-white font-bold text-lg mb-4">
                      {step.step}
                    </div>
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <Icon className="h-5 w-5 text-[#4F6CFF]" />
                      <h3 className="text-lg font-bold text-[#1A1F3C]">{step.title}</h3>
                    </div>
                    <p className="text-sm text-[#1A1F3C]/60 leading-relaxed">{step.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Sync Table */}
        <section className="py-16 md:py-20">
          <div className="container max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1A1F3C] mb-4 text-center">
              {isPt ? "O que é sincronizado" : "What gets synchronized"}
            </h2>
            <p className="text-center text-[#1A1F3C]/60 mb-10 max-w-2xl mx-auto">
              {isPt
                ? "RevOps AI importa todas as entidades relevantes do seu CRM para gerar análises completas de receita, pipeline e performance."
                : "RevOps AI imports all relevant entities from your CRM to generate comprehensive revenue, pipeline, and performance analytics."}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-[#1A1F3C]/10">
                    <th className="text-left py-3 px-4 text-sm font-bold text-[#1A1F3C]">{isPt ? "Entidade" : "Entity"}</th>
                    <th className="text-left py-3 px-4 text-sm font-bold text-[#1A1F3C]">{isPt ? "Dados sincronizados" : "Synced data"}</th>
                    <th className="text-left py-3 px-4 text-sm font-bold text-[#1A1F3C]">{isPt ? "Frequência" : "Frequency"}</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row, i) => (
                    <tr key={i} className="border-b border-[#1A1F3C]/5">
                      <td className="py-3 px-4 text-sm font-medium text-[#1A1F3C]">{row.entity}</td>
                      <td className="py-3 px-4 text-sm text-[#1A1F3C]/70">{row.desc}</td>
                      <td className="py-3 px-4 text-sm text-[#4F6CFF] font-medium">{row.frequency}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-20 bg-[#F4F5FF]/50">
          <div className="container max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1A1F3C] mb-10 text-center">
              {isPt ? "Perguntas frequentes sobre integrações" : "Frequently asked questions about integrations"}
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, i) => (
                <div key={i} className="rounded-xl border border-[#1A1F3C]/10 p-6 bg-white">
                  <h3 className="text-base font-bold text-[#1A1F3C] mb-2">{faq.q}</h3>
                  <p className="text-sm text-[#1A1F3C]/70 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-20 bg-[#1A1F3C] text-white text-center">
          <div className="container max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">
              {isPt ? "Conecte seu CRM e veja a mágica acontecer" : "Connect your CRM and watch the magic happen"}
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-[#4F6CFF] hover:bg-[#3D5AE8] text-white">
                <Link to="/signup">
                  {isPt ? "Teste Grátis" : "Start Free Trial"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10">
                <Link to="/demo">{isPt ? "Agendar Demo" : "Book a Demo"}</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <LandingFooter />
    </div>
  );
}
